/**
 * Pomaria Beauty — homepage behaviours.
 * Dependency-free custom elements so the theme stays fast and Horizon-safe.
 */
(function () {
  'use strict';

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------------------------------------------
   * <pomaria-slideshow> — hero / testimonial rotator
   * ------------------------------------------------------------- */
  class PomariaSlideshow extends HTMLElement {
    connectedCallback() {
      this.slides = Array.from(this.querySelectorAll('[data-pomaria-slide]'));
      if (this.slides.length === 0) return;

      this.dots = Array.from(this.querySelectorAll('[data-pomaria-dot]'));
      this.index = 0;
      this.timer = null;

      this.intervalMs = parseInt(this.getAttribute('interval'), 10) || 7000;
      this.shouldAutoplay = this.hasAttribute('autoplay') && !reduceMotion && this.slides.length > 1;

      this.onPrev = () => this.go(this.index - 1, true);
      this.onNext = () => this.go(this.index + 1, true);

      var prev = this.querySelector('[data-pomaria-prev]');
      var next = this.querySelector('[data-pomaria-next]');
      if (prev) prev.addEventListener('click', this.onPrev);
      if (next) next.addEventListener('click', this.onNext);

      this.dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          this.go(parseInt(dot.getAttribute('data-pomaria-dot'), 10), true);
        });
      });

      this.addEventListener('mouseenter', () => this.pause());
      this.addEventListener('mouseleave', () => this.play());
      this.addEventListener('focusin', () => this.pause());
      this.addEventListener('focusout', () => this.play());

      // Pause while off-screen so we never animate work nobody can see.
      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            entries.forEach((entry) => (entry.isIntersecting ? this.play() : this.pause()));
          },
          { threshold: 0.15 }
        );
        this.observer.observe(this);
      }

      this.bindSwipe();
      // Signals to CSS that slides may now be cross-faded rather than
      // rendered as a single static first slide.
      this.classList.add('is-initialized');
      this.render();
      this.play();
    }

    disconnectedCallback() {
      this.pause();
      if (this.observer) this.observer.disconnect();
    }

    bindSwipe() {
      var startX = null;
      this.addEventListener(
        'touchstart',
        (e) => {
          startX = e.touches[0].clientX;
        },
        { passive: true }
      );
      this.addEventListener(
        'touchend',
        (e) => {
          if (startX === null) return;
          var delta = e.changedTouches[0].clientX - startX;
          if (Math.abs(delta) > 45) this.go(this.index + (delta < 0 ? 1 : -1), true);
          startX = null;
        },
        { passive: true }
      );
    }

    go(next, userInitiated) {
      var total = this.slides.length;
      this.index = ((next % total) + total) % total;
      this.render();
      if (userInitiated) this.restart();
    }

    render() {
      this.slides.forEach((slide, i) => {
        var active = i === this.index;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        // Keep hidden slides out of the tab order.
        slide.querySelectorAll('a, button').forEach((el) => {
          if (active) {
            el.removeAttribute('tabindex');
          } else {
            el.setAttribute('tabindex', '-1');
          }
        });
      });

      this.dots.forEach((dot, i) => {
        var active = i === this.index;
        dot.classList.toggle('is-active', active);
        dot.setAttribute('aria-selected', active ? 'true' : 'false');
      });
    }

    play() {
      if (!this.shouldAutoplay || this.timer) return;
      this.timer = setInterval(() => this.go(this.index + 1), this.intervalMs);
      this.classList.add('is-playing');
    }

    pause() {
      if (!this.timer) return;
      clearInterval(this.timer);
      this.timer = null;
      this.classList.remove('is-playing');
    }

    restart() {
      this.pause();
      this.play();
    }
  }

  if (!customElements.get('pomaria-slideshow')) {
    customElements.define('pomaria-slideshow', PomariaSlideshow);
  }

  /* ---------------------------------------------------------------
   * Scroll reveal — opt-in via [data-pomaria-reveal]
   * ------------------------------------------------------------- */
  function initReveal(root) {
    var targets = (root || document).querySelectorAll('[data-pomaria-reveal]:not(.pomaria-revealed)');
    if (targets.length === 0) return;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach((el) => el.classList.add('pomaria-revealed'));
      return;
    }

    var io = new IntersectionObserver(
      (entries, observer) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('pomaria-revealed');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }
    );

    targets.forEach((el) => io.observe(el));
  }

  /* ---------------------------------------------------------------
   * <pomaria-marquee> — duplicates its track for a seamless loop
   * ------------------------------------------------------------- */
  class PomariaMarquee extends HTMLElement {
    connectedCallback() {
      var track = this.querySelector('[data-pomaria-marquee-track]');
      if (!track || track.dataset.cloned === 'true') return;
      var clone = track.cloneNode(true);
      clone.setAttribute('aria-hidden', 'true');
      clone.dataset.cloned = 'true';
      track.dataset.cloned = 'true';
      this.appendChild(clone);
    }
  }

  if (!customElements.get('pomaria-marquee')) {
    customElements.define('pomaria-marquee', PomariaMarquee);
  }

  /* ---------------------------------------------------------------
   * Boot
   * ------------------------------------------------------------- */
  function boot() {
    initReveal(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-run after theme-editor section reloads.
  document.addEventListener('shopify:section:load', function (event) {
    initReveal(event.target);
  });
})();
